/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Center,
  Progress,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  getActivityDetailColumnLayout,
  getActivityDetailTableMinWidth,
  getVisibleColumnIds,
  getVisibleStageGroups,
} from './activity-detail-column-layout';
import { ActivityDetailTableCell } from './activity-detail-table-cell';

const HEADER_BG = '#EEEEF8';
const HEADER_COLOR = '#373F41';
const BORDER_COLOR = '#D9D9D9';
const GROUP_BORDER_COLOR = '#BDBDC9';

const SUB_HEADERS = ['Qty', 'Harga', 'Subtotal'] as const;

const STAGE_GROUP_START_IDS = new Set([
  'checkout_qty',
  'so_qty',
  'pgi_qty',
  'pod_qty',
  'product_storage',
]);

const cellBorder = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: BORDER_COLOR,
};

const groupStartBorder = {
  borderLeftWidth: '2px',
  borderLeftColor: GROUP_BORDER_COLOR,
};

const headerCellProps = {
  ...cellBorder,
  bg: HEADER_BG,
  color: HEADER_COLOR,
  fontSize: 'xs',
  fontWeight: '600',
  whiteSpace: 'nowrap' as const,
  py: 2,
  lineHeight: 'short',
  textAlign: 'center' as const,
  verticalAlign: 'middle' as const,
};

const subHeaderCellProps = {
  ...headerCellProps,
  fontWeight: '500',
};

const bodyCellProps = {
  ...cellBorder,
  py: 2,
  verticalAlign: 'top' as const,
  bg: 'white',
};

type ActivityDetailGroupedTableProps<Data extends object> = {
  data: Data[];
  columns: ColumnDef<Data, any>[];
  isLoading?: boolean;
  isExpanded?: boolean;
};

export function ActivityDetailGroupedTable<Data extends object>({
  data,
  columns,
  isLoading,
  isExpanded = false,
}: ActivityDetailGroupedTableProps<Data>) {
  const visibleColumnIds = getVisibleColumnIds(isExpanded);
  const visibleStageGroups = getVisibleStageGroups(isExpanded);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  const leafCount = table.getAllLeafColumns().length;
  const selectHeader = table
    .getHeaderGroups()[0]
    ?.headers.find((header) => header.column.id === 'select');

  return (
    <TableContainer overflowX="auto" overflowY="unset">
      {isLoading ? (
        <Progress size="xs" isIndeterminate colorScheme="messenger" />
      ) : null}
      <Table
        data-testid="activity-detail-grouped-table"
        data-expanded={isExpanded ? 'true' : 'false'}
        size="sm"
        variant="unstyled"
        sx={{
          borderCollapse: 'collapse',
          minWidth: getActivityDetailTableMinWidth(isExpanded),
          tableLayout: 'fixed',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: BORDER_COLOR,
          'tbody tr:nth-of-type(even) td': {
            bg: '#FAFAFC',
          },
        }}
      >
        <colgroup>
          {visibleColumnIds.map((columnId) => (
            <col
              key={columnId}
              style={{
                width: getActivityDetailColumnLayout(columnId, isExpanded)
                  .width,
              }}
            />
          ))}
        </colgroup>
        <Thead position="sticky" top={0} zIndex="docked">
          <Tr>
            <Th
              rowSpan={2}
              {...headerCellProps}
              px={getActivityDetailColumnLayout('select', isExpanded).px ?? 2}
            >
              <Center>
                {selectHeader
                  ? flexRender(
                      selectHeader.column.columnDef.header,
                      selectHeader.getContext()
                    )
                  : null}
              </Center>
            </Th>
            <Th
              rowSpan={2}
              {...headerCellProps}
              px={
                getActivityDetailColumnLayout('item_code', isExpanded).px ?? 2
              }
            >
              Kode Material
            </Th>
            <Th rowSpan={2} {...headerCellProps} px={2}>
              Produk
            </Th>
            <Th rowSpan={2} {...headerCellProps} px={2}>
              Catatan Produk
            </Th>
            <Th rowSpan={2} {...headerCellProps} px={2}>
              Valuation type
            </Th>
            {visibleStageGroups.map((group) => (
              <Th
                key={group.key}
                colSpan={3}
                {...headerCellProps}
                {...groupStartBorder}
                px={2}
              >
                {group.subLabel
                  ? `${group.label} (${group.subLabel})`
                  : group.label}
              </Th>
            ))}
            <Th rowSpan={2} {...headerCellProps} {...groupStartBorder} px={2}>
              Penyimpanan
            </Th>
            <Th rowSpan={2} {...headerCellProps} px={2}>
              diubah
            </Th>
            {isExpanded ? (
              <Th rowSpan={2} {...headerCellProps} px={2}>
                Alasan Pembatalan
              </Th>
            ) : null}
          </Tr>
          <Tr>
            {visibleStageGroups.flatMap((group) =>
              SUB_HEADERS.map((subHeader, subIndex) => (
                <Th
                  key={`${group.key}-${subHeader}`}
                  {...subHeaderCellProps}
                  {...(subIndex === 0 ? groupStartBorder : {})}
                  px={subHeader === 'Qty' ? 1 : 2}
                >
                  {subHeader}
                </Th>
              ))
            )}
          </Tr>
        </Thead>
        <Tbody>
          {data.length === 0 ? (
            <Tr>
              <Td colSpan={leafCount} {...bodyCellProps} px={2}>
                <Center h="100%" py={8}>
                  <Text fontSize="lg">Data tidak ditemukan</Text>
                </Center>
              </Td>
            </Tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const columnId = cell.column.id;
                  const layout = getActivityDetailColumnLayout(
                    columnId,
                    isExpanded
                  );
                  const isStageGroupStart = STAGE_GROUP_START_IDS.has(columnId);

                  return (
                    <Td
                      key={cell.id}
                      textAlign={layout.align}
                      {...bodyCellProps}
                      px={layout.px ?? 2}
                      {...(isStageGroupStart ? groupStartBorder : {})}
                    >
                      <ActivityDetailTableCell
                        align={layout.align}
                        allowWrap={layout.allowWrap}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </ActivityDetailTableCell>
                    </Td>
                  );
                })}
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
